/**
 * Every stored value the UI shows passes through here. Backend enums are wire
 * format; an untranslated one reaching the screen is a bug.
 */

function titleCase(value: string): string {
  return value
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word, index) => (index === 0 ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");
}

/** Falls back to title-cased words so an unmapped value is still readable. */
function lookup(map: Record<string, string>, value: string | null | undefined): string {
  if (!value) return "";
  return map[value] ?? titleCase(value);
}

const USER_STATUS: Record<string, string> = {
  REGISTRATION_PENDING: "Awaiting approval",
  PENDING: "Awaiting approval",
  REGISTRATION_REJECTED: "Registration declined",
  REJECTED: "Declined",
  APPROVED: "Approved",
  UNVARIFIED: "Email not confirmed",
};

export const userStatusLabel = (value: string | null | undefined) => lookup(USER_STATUS, value);

export function userStatusColour(
  value: string | null | undefined,
): "default" | "success" | "warning" | "error" {
  switch (value) {
    case "APPROVED":
      return "success";
    case "PENDING":
    case "REGISTRATION_PENDING":
      return "warning";
    case "REJECTED":
    case "REGISTRATION_REJECTED":
      return "error";
    default:
      return "default";
  }
}

const ROLE: Record<string, string> = {
  SUPER_ADMIN: "Super administrator",
  ADMIN_USER: "Administrator",
  APP_USER: "App member",
};

/** Administrator-created roles already have human names and pass through. */
export const roleLabel = (value: string | null | undefined) =>
  value ? (ROLE[value] ?? value) : "";

const SURVEY_STATUS: Record<string, string> = {
  PENDING: "Draft",
  PUBLISHED: "Open",
  COMPLETED: "Closed",
  CANCELLED: "Cancelled",
};

export const surveyStatusLabel = (value: string | null | undefined) => lookup(SURVEY_STATUS, value);

export function surveyStatusColour(
  value: string | null | undefined,
): "default" | "success" | "info" | "error" {
  switch (value) {
    case "PUBLISHED":
      return "success";
    case "COMPLETED":
      return "info";
    case "CANCELLED":
      return "error";
    default:
      return "default";
  }
}

const QUESTION_TYPE: Record<string, string> = {
  SHORT_ANSWERS: "Short answer",
  LONG_ANSWERS: "Long answer",
  SINGLE_SELECTION_DROPDOWN: "Pick one (dropdown)",
  SINGLE_SELECTION_RADIO: "Pick one (list)",
  SINGLE_SELECTION_WITH_COMMENT_DROPDOWN: "Pick one, with a comment (dropdown)",
  SINGLE_SELECTION_WITH_COMMENT_RADIO: "Pick one, with a comment (list)",
  MULTIPLE_SELECTION: "Pick several",
  RATING: "Rating",
  RANKING_MATRIX: "Ranking grid",
};

export const questionTypeLabel = (value: string | null | undefined) => lookup(QUESTION_TYPE, value);

const QUESTION_TYPE_HINT: Record<string, string> = {
  SHORT_ANSWERS: "A single line of text.",
  LONG_ANSWERS: "A paragraph.",
  SINGLE_SELECTION_DROPDOWN: "One answer from a dropdown. Good for long lists.",
  SINGLE_SELECTION_RADIO: "One answer, all options visible. Good for a handful.",
  SINGLE_SELECTION_WITH_COMMENT_DROPDOWN: "One answer from a dropdown, plus a free-text box.",
  SINGLE_SELECTION_WITH_COMMENT_RADIO: "One answer from a visible list, plus a free-text box.",
  MULTIPLE_SELECTION: "Any number of answers from the list.",
  RATING: "A score on a scale.",
  RANKING_MATRIX: "Rows scored against columns.",
};

export const questionTypeHint = (value: string | null | undefined) =>
  value ? (QUESTION_TYPE_HINT[value] ?? "") : "";

/** Types whose answers come from a fixed list, so they need options. */
export const CHOICE_TYPES = new Set([
  "SINGLE_SELECTION_DROPDOWN",
  "SINGLE_SELECTION_RADIO",
  "SINGLE_SELECTION_WITH_COMMENT_RADIO",
  "SINGLE_SELECTION_WITH_COMMENT_DROPDOWN",
  "MULTIPLE_SELECTION",
  "RANKING_MATRIX",
]);

const NOTIF_TYPE: Record<string, string> = {
  TEXT: "Text",
  IMAGE: "Image",
  VIDEO: "Video",
};

export const notificationTypeLabel = (value: string | null | undefined) =>
  lookup(NOTIF_TYPE, value);

export const mediaTypeLabel = (value: string | null | undefined) => lookup(NOTIF_TYPE, value);

const TEMPLATE_AUDIENCE: Record<string, string> = {
  USER: "Members",
  ADMIN: "Administrators",
  SUPER_ADMIN: "Super administrators",
};

export const templateAudienceLabel = (value: string | null | undefined) =>
  lookup(TEMPLATE_AUDIENCE, value);

const AUTHORITY: Record<string, string> = {
  LOGIN: "Sign in",
  VIEW_USER: "View users",
  CREATE_USER: "Add users",
  EDIT_USER: "Edit users",
  DELETE_USER: "Delete users",
  REVIEW_USER: "Approve registrations",
  DISABLE_USER: "Disable accounts",
  VIEW_ROLE: "View roles",
  CREATE_ROLE: "Add roles",
  EDIT_ROLE: "Edit roles",
  DELETE_ROLE: "Delete roles",
  VIEW_SURVEY: "View surveys",
  CREATE_SURVEY: "Create surveys",
  EDIT_SURVEY: "Edit surveys",
  DELETE_SURVEY: "Delete surveys",
  PUBLISH_SURVEY: "Publish surveys",
  DOWNLOAD_SURVEY_RESPONSE: "Download survey responses",
  VIEW_REGISTRATION_PROPERTY: "View registration form",
  EDIT_REGISTRATION_PROPERTY: "Edit registration form",
  VIEW_TEMPLATES_LIST: "View template list",
  VIEW_TEMPLATE: "View templates",
  EDIT_TEMPLATE: "Edit templates",
  DELETE_TEMPLATE: "Delete templates",
  EDIT_TEMPLATE_TYPE: "Change template audience",
  VIEW_NOTIFICATIONS: "View messages",
  ADD_NOTIFICATION: "Send messages",
  EDIT_NOTIFICATION: "Edit messages",
  DELETE_NOTIFICATION: "Delete messages",
  VIEW_NEWSFEED: "View newsfeed",
  CREATE_NEWSFEED: "Add newsfeed posts",
  EDIT_NEWSFEED: "Edit newsfeed posts",
  DELETE_NEWSFEED: "Delete newsfeed posts",
  VIEW_CLERGY: "View clergy directory",
  VIEW_PARISH: "View parish directory",
  VIEW_CALENDAR: "View calendars",
  APPROVE_CALENDAR: "Approve calendars",
};

export const authorityLabel = (value: string | null | undefined) => lookup(AUTHORITY, value);

/** The area of the console a permission governs, for grouping the roles grid. */
const AUTHORITY_GROUP: Record<string, string> = {
  LOGIN: "Access",
  VIEW_USER: "Users",
  CREATE_USER: "Users",
  EDIT_USER: "Users",
  DELETE_USER: "Users",
  REVIEW_USER: "Users",
  DISABLE_USER: "Users",
  VIEW_ROLE: "Roles",
  CREATE_ROLE: "Roles",
  EDIT_ROLE: "Roles",
  DELETE_ROLE: "Roles",
  VIEW_SURVEY: "Surveys",
  CREATE_SURVEY: "Surveys",
  EDIT_SURVEY: "Surveys",
  DELETE_SURVEY: "Surveys",
  PUBLISH_SURVEY: "Surveys",
  DOWNLOAD_SURVEY_RESPONSE: "Surveys",
  VIEW_REGISTRATION_PROPERTY: "Registration",
  EDIT_REGISTRATION_PROPERTY: "Registration",
  VIEW_TEMPLATES_LIST: "Templates",
  VIEW_TEMPLATE: "Templates",
  EDIT_TEMPLATE: "Templates",
  DELETE_TEMPLATE: "Templates",
  EDIT_TEMPLATE_TYPE: "Templates",
  VIEW_NOTIFICATIONS: "Messages",
  ADD_NOTIFICATION: "Messages",
  EDIT_NOTIFICATION: "Messages",
  DELETE_NOTIFICATION: "Messages",
  VIEW_NEWSFEED: "Newsfeed",
  CREATE_NEWSFEED: "Newsfeed",
  EDIT_NEWSFEED: "Newsfeed",
  DELETE_NEWSFEED: "Newsfeed",
  VIEW_CLERGY: "Directory",
  VIEW_PARISH: "Directory",
  VIEW_CALENDAR: "Calendars",
  APPROVE_CALENDAR: "Calendars",
};

export const AUTHORITY_GROUP_ORDER = [
  "Access",
  "Users",
  "Roles",
  "Surveys",
  "Newsfeed",
  "Messages",
  "Templates",
  "Registration",
  "Directory",
  "Calendars",
  "Other",
];

export const authorityGroup = (value: string) => AUTHORITY_GROUP[value] ?? "Other";

/**
 * Message and newsfeed bodies are stored as editor HTML. Lists show them as
 * text, which means decoding the entities too -- raw `&nbsp;` and `&#39;` on
 * screen is what made the messages list look broken.
 */
export function htmlToText(html: string | null | undefined): string {
  if (!html) return "";
  const withoutTags = html.replace(/<[^>]*>/g, " ");
  const decoded =
    typeof document === "undefined"
      ? withoutTags
      : (() => {
          const element = document.createElement("textarea");
          element.innerHTML = withoutTags;
          return element.value;
        })();
  return decoded.replace(/\s+/g, " ").trim();
}
